import { LAYOUT_WORDS, parseLayout, pickLayoutOptions, type LayoutOptions } from '../../../common/utils/pattern-name';
import { v4 as uuidv4 } from 'uuid';
import type { CreateProductV2Payload, BulkOperationItem, SurfaceFinish } from '../../../common/models/product-v2.model';
import type { VariantAxisConfig } from '../../../common/models/product-family.model';
import { normalizeDisplayUnit, parseDimensions, plankCm } from '../../../common/utils/units';
import { isOpusImageCountValid } from '../../../modules/vendor/edit-product-v3/utils/opusImages';
import {
    normalizeCurrency,
    normalizePriceUnit,
    parseDecimal,
    roundMoney,
    type ProductPack,
    type ProductPrice,
    type ProductSample,
} from '../../../common/utils/product-pricing';

/**
 * Lightweight import types - Direct CSV to API payload
 * Replaces heavy EditProduct/ProductVariant flow for imports
 */

// A single variant (one row in CSV with unique color/size)
export interface ImportVariant {
    id: string;                 // UUID for tracking
    sku: string;
    requiredFiles: string[];    // filenames from CSV 'files' column
    matchedFiles: File[];       // matched at runtime when images load
    isSelected: boolean;        // UI selection state
    isVisible: boolean;

    // Dynamic axes from family config (key = axis name, value = parsed value)
    // For 'dimensions' valueType: { width, height, depth?, unit }
    // For 'string'/'number' valueType: plain string/number
    axisValues: Record<string, any>;

    // Surface-specific
    groutColor?: string;
    groutSize?: number;
    groutEditable?: boolean;    // shoppers may change the grout in the visualizer (grout v2)
    finish?: SurfaceFinish;     // how it reflects light, from the 'finish' column (see parseCsvFinish)
    layoutOptions?: LayoutOptions; // words after the layout ("stackbond:mirror"), see parseLayout
    layoutProblem?: string;     // words the layout's pattern does not accept, explained
    plankGroupId?: string;      // shared by variants that differ only on non-grouping axes (pattern) — material_<hex> format

    // Non-surface families
    modelFile?: string;         // filename of 3D model from CSV 'model_file' column
    matchedModelFile?: File;    // matched 3D model file at runtime

    // What the row sells for (price, currency, price_unit, m2_per_box, pieces_per_box,
    // sample_available, sample_price columns), sent as commerce.price / pack / sample.
    price?: ProductPrice;
    pack?: ProductPack;
    sample?: ProductSample;
    priceWarnings?: PriceWarning[]; // cells that could not be used: shown on the row, never guessed
}

/**
 * A price cell the import could not use. The value is left out of the upload
 * rather than guessed, and the row says why (PRODUCT_PRICING.CSV.WARNING_*).
 */
export interface PriceWarning {
    code: 'NO_CURRENCY' | 'BAD_CURRENCY' | 'UNREADABLE' | 'UNKNOWN_UNIT';
    /** The CSV column. */
    field: string;
    /** The cell as written. */
    value: string;
}

// A product with its variants (rows with same name grouped together)
export interface ImportProduct {
    id: string;                 // UUID for UI tracking
    plankGroupId: string;       // shared across variants for image grouping

    // Family reference (V3)
    familyId: string;           // ProductFamily._id
    familySlug: string;         // ProductFamily.slug

    // From CSV
    name: string;
    type: string;
    brand: string;
    description: string;
    link: string;
    addToCartLink: string;
    collections: string[];
    applicationTypes: string[];

    // Variants (different color/size combos from CSV rows with same name)
    variants: ImportVariant[];

    // UI state
    isSelected: boolean;
    status: 'pending' | 'uploading' | 'done' | 'error';
    errorMessage?: string;
    layoutError?: boolean;      // a row's layout has words its pattern does not accept: not uploadable

    // Extracted at create time
    extractedColors?: string[];
}

// File prepared for upload
export interface PreparedFile {
    file: File;
    metaType: 'plank' | 'thumbnail' | 'original' | 'product_image' | 'model3d';
    productId: string;
}

// Result of building API payload (V3 format)
export interface APIPayloadResult {
    products: CreateProductV2Payload[];
    files: PreparedFile[];
}

// Result of building V2 bulk operations wrapper
export interface V2OperationsResult {
    operations: {
        productGroupId: string;
        familyId: string;
        familySlug: string;
        operations: BulkOperationItem[];
    };
    files: PreparedFile[];
}

/** Why a layout cell cannot be used, or undefined when it can. */
export function describeLayoutProblem(raw: unknown, pattern: string | undefined, unknownWords: string[]): string | undefined {
    if (unknownWords.length === 0) return undefined;
    const allowed = (pattern && LAYOUT_WORDS[pattern]) || [];
    const words = unknownWords.map((w) => `"${w}"`).join(', ');
    return allowed.length
        ? `Layout "${String(raw).trim()}": ${words} not known for ${pattern}. Allowed: ${allowed.join(', ')}.`
        : `Layout "${String(raw).trim()}": ${pattern || 'this pattern'} takes no options (${words}).`;
}

/**
 * Why a row's files cannot be laid in its pattern, or undefined when they can.
 * Cabochon lists the octagons then the corner squares, as many of each (the
 * generator halves the list); opus lists its four pieces largest first, or one
 * image for all four. Rows without files share another row's images.
 */
export function describeImageCountProblem(pattern: string | undefined, fileCount: number): string | undefined {
    if (fileCount === 0) return undefined;
    if (pattern === 'cabochon' && fileCount % 2 === 1) {
        return `Cabochon needs the octagon images then the corner square images, as many of each: this row has ${fileCount} files.`;
    }
    if (pattern === 'opus' && !isOpusImageCountValid(fileCount)) {
        return `Opus needs 4 images (2x2, 3x2, 2x1 and 1x1 pieces, in that order) or 1 for all: this row has ${fileCount} files.`;
    }
    return undefined;
}

/** A CSV yes/no cell: true for true, 1, "true", "yes", "1" (any case). */
export function isCsvTrue(value: unknown): boolean {
    if (value === true || value === 1) return true;
    return typeof value === 'string' && ['true', 'yes', '1'].includes(value.trim().toLowerCase());
}

/**
 * The words a finish is written in, accent-free and lower case, in the app's
 * languages and as tile shops print them: "Mate", "Brillant", "Poli effet miroir".
 */
const FINISH_WORDS: [SurfaceFinish, string[]][] = [
    ['handmade', ['handmade', 'zellige', 'artisanal', 'artisanale', 'hecho a mano', 'artesanal']],
    ['lacquered', ['lacquered', 'laque', 'laquee', 'vernis', 'varnished', 'lacado', 'barnizado', 'ورنيش']],
    ['glossy', ['glossy', 'gloss', 'polished', 'brillant', 'brillante', 'poli', 'polie', 'miroir', 'lappato', 'pulido', 'brillo', 'لامع', 'مصقول']],
    ['satin', ['satin', 'satine', 'satinee', 'satinado', 'ساتان']],
    ['matte', ['matte', 'matt', 'mat', 'mate', 'mats', 'structure', 'structuree', 'brosse', 'brossee', 'adouci', 'honed', 'مطفي']],
];

/** A CSV finish cell: one of the finishes, in any of those words; undefined if empty or unknown. */
export function parseCsvFinish(value: unknown): SurfaceFinish | undefined {
    if (typeof value !== 'string' || !value.trim()) return undefined;
    // Whole words only ("mat" is not in "material"), accents dropped ("satinée").
    const text = ` ${value.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ')} `;
    return FINISH_WORDS.find(([, words]) => words.some((w) => text.includes(` ${w} `)))?.[0];
}

/** A CSV yes/no cell that says no: false, 0, "false", "no", "non" (any case). */
export function isCsvFalse(value: unknown): boolean {
    if (value === false || value === 0) return true;
    return typeof value === 'string' && ['false', 'no', '0', 'non'].includes(value.trim().toLowerCase());
}

const csvCell = (value: unknown): string | undefined => {
    if (value === undefined || value === null) return undefined;
    const text = String(value).trim();
    return text === '' ? undefined : text;
};

/** The currency a price cell states itself ("24,90 €", "24.90 EUR"), if any. */
function currencyInCell(cell: string): string | undefined {
    if (/€/.test(cell)) return 'EUR';
    if (/£/.test(cell)) return 'GBP';
    const code = cell.match(/(?:^|[\d\s])([A-Za-z]{3})$/)?.[1];
    return code ? normalizeCurrency(code) : undefined;
}

const FREE_WORDS = /^(free|gratuit|gratuite|gratis|مجاني|مجانا|مجانًا)$/i;

/**
 * Price, packaging and sample from one CSV row — one variant.
 *
 * Decimals read with a dot or a comma ("24,90"). Nothing is invented: a price
 * without a currency is not sent, and neither is a cell that does not read as
 * a number; each comes back as a warning instead. A unit that is not known is
 * left out, and the price goes without it. A sample price alone says samples
 * can be ordered.
 */
export function parseRowCommerce(row: Record<string, unknown>): {
    price?: ProductPrice;
    pack?: ProductPack;
    sample?: ProductSample;
    warnings: PriceWarning[];
} {
    const warnings: PriceWarning[] = [];
    const warn = (code: PriceWarning['code'], field: string, value: string) => warnings.push({ code, field, value });

    // Currency: the column, else what the price cell itself says.
    const currencyCell = csvCell(row.currency);
    let currency = currencyCell ? normalizeCurrency(currencyCell) : undefined;
    if (currencyCell && !currency) warn('BAD_CURRENCY', 'currency', currencyCell);

    let price: ProductPrice | undefined;
    const priceCell = csvCell(row.price);
    if (priceCell !== undefined) {
        if (!currency && !currencyCell) currency = currencyInCell(priceCell);
        const amount = typeof row.price === 'number' ? row.price : parseDecimal(priceCell);
        if (amount === undefined || !(amount > 0)) {
            warn('UNREADABLE', 'price', priceCell);
        } else if (!currency) {
            // Never invent the currency: the price stays out and the row says why
            // (a currency cell that did not read has already said so).
            if (!currencyCell) warn('NO_CURRENCY', 'price', priceCell);
        } else {
            const unitCell = csvCell(row.price_unit);
            const unit = unitCell ? normalizePriceUnit(unitCell) : undefined;
            if (unitCell && !unit) warn('UNKNOWN_UNIT', 'price_unit', unitCell);
            price = { amount: roundMoney(amount), currency, ...(unit ? { unit } : {}), source: 'import' };
        }
    }

    const readCount = (field: 'm2_per_box' | 'pieces_per_box', integer: boolean): number | undefined => {
        const cell = csvCell(row[field]);
        if (cell === undefined) return undefined;
        const value = typeof row[field] === 'number' ? (row[field] as number) : parseDecimal(cell);
        if (value === undefined || !(value > 0) || (integer && !Number.isInteger(value))) {
            warn('UNREADABLE', field, cell);
            return undefined;
        }
        return integer ? value : Math.round(value * 10000) / 10000;
    };
    const m2PerBox = readCount('m2_per_box', false);
    const piecesPerBox = readCount('pieces_per_box', true);
    const pack: ProductPack | undefined = m2PerBox || piecesPerBox
        ? { ...(m2PerBox ? { m2PerBox } : {}), ...(piecesPerBox ? { piecesPerBox } : {}), source: 'import' }
        : undefined;

    let sample: ProductSample | undefined;
    const availableCell = csvCell(row.sample_available);
    let available: boolean | undefined;
    if (availableCell !== undefined) {
        if (isCsvTrue(row.sample_available)) available = true;
        else if (isCsvFalse(row.sample_available)) available = false;
        else warn('UNREADABLE', 'sample_available', availableCell);
    }
    const samplePriceCell = csvCell(row.sample_price);
    let samplePrice: number | undefined;
    if (samplePriceCell !== undefined) {
        samplePrice = FREE_WORDS.test(samplePriceCell)
            ? 0
            : typeof row.sample_price === 'number' && row.sample_price >= 0
                ? row.sample_price
                : parseDecimal(samplePriceCell);
        if (samplePrice === undefined) warn('UNREADABLE', 'sample_price', samplePriceCell);
    }
    if (available === undefined && samplePrice !== undefined) available = true;
    if (available !== undefined) {
        const sampleCurrency = currency ?? (samplePriceCell ? currencyInCell(samplePriceCell) : undefined);
        if (available && samplePrice !== undefined && samplePrice > 0 && !sampleCurrency) {
            // A paid sample with no currency: say samples exist, not what they cost.
            warn('NO_CURRENCY', 'sample_price', samplePriceCell!);
            samplePrice = undefined;
        }
        sample = {
            available,
            ...(available && samplePrice !== undefined
                ? { price: roundMoney(samplePrice), ...(sampleCurrency ? { currency: sampleCurrency } : {}) }
                : {}),
        };
    }

    return {
        ...(price ? { price } : {}),
        ...(pack ? { pack } : {}),
        ...(sample ? { sample } : {}),
        warnings,
    };
}

/**
 * Generate a fresh plankGroupId. Backend recognizes the format via regex:
 * /^(material_[a-f0-9]+)_(\d+)\./i — any number of hex chars works. We use
 * the full uuid (32 hex chars) to make accidental collisions across imports
 * effectively impossible.
 */
export function generatePlankGroupId(): string {
    return `material_${uuidv4().replace(/-/g, '')}`;
}

/**
 * Check if a family slug is a surface product family
 */
export function isSurfaceFamily(familySlug: string): boolean {
    return familySlug === 'surface-products';
}

/**
 * Check if a family slug requires 3D models (furniture, lighting)
 */
export function isModel3dFamily(familySlug: string): boolean {
    return familySlug === 'furniture' || familySlug === 'lighting';
}

/**
 * Get visualization type for a family slug
 */
export function getVisualizationTypeForFamily(familySlug: string): 'surface' | 'model3d' | 'image_only' {
    if (isSurfaceFamily(familySlug)) return 'surface';
    if (isModel3dFamily(familySlug)) return 'model3d';
    return 'image_only';
}

/**
 * Parse a CSV column value into the correct axis value based on axis config.
 * - 'dimensions' valueType: parse WxH or WxHxD into { width, height, depth?, unit }
 * - 'string' valueType: plain string
 * - 'number' valueType: parsed number
 */
function parseAxisValue(rawValue: any, axisConfig: VariantAxisConfig, defaultUnit: string = 'cm'): any {
    if (rawValue === undefined || rawValue === null || rawValue === '') return undefined;

    if (axisConfig.valueType === 'dimensions') {
        // "30x60", "30x60cm", "12x24in", '12x24"'; no unit written → the vendor's unit.
        const parsed = parseDimensions(rawValue, defaultUnit);
        if (parsed) return parsed;
        const parts = String(rawValue).split('x').map((n: string) => parseFloat(n) || 0);
        const result: Record<string, any> = {
            width: parts[0] || 0,
            height: parts[1] || 0,
            unit: normalizeDisplayUnit(defaultUnit),
        };
        if (parts.length >= 3 && parts[2]) {
            result.depth = parts[2];
        }
        return result;
    }

    if (axisConfig.valueType === 'number') {
        return parseFloat(String(rawValue)) || 0;
    }

    // 'string' valueType — plain string
    return String(rawValue);
}

/**
 * The key that decides which variants share a plank image set (plankGroupId).
 *
 * The family says which axes group (`isGroupingAxis`) — for surfaces that is
 * colour and size, so only pattern variants share images. Keying on size alone
 * put every colour of a size into one group: their images were pooled, and each
 * variant ended up showing the first colour's picture.
 *
 * A family that flags no axis falls back to every axis except pattern, which is
 * what the import has always documented: patterns share, colours do not.
 */
function getPlankGroupKey(axisValues: Record<string, any>, variantAxes: VariantAxisConfig[]): string {
    const flagged = variantAxes.filter(a => a.enabled && a.isGroupingAxis);
    const groupingAxes = flagged.length > 0
        ? flagged
        : variantAxes.filter(a => a.enabled && a.name !== 'pattern');
    return groupingAxes
        .map(a => {
            const value = axisValues[a.name];
            if (value && typeof value === 'object') {
                return `${a.name}:${value.width || 0}x${value.height || 0}`;
            }
            return `${a.name}:${String(value ?? '').trim().toLowerCase()}`;
        })
        .join('|');
}

/**
 * Parse CSV rows into ImportProduct array
 * Groups rows by product name - same name = same product with multiple variants
 * Reads axis names and types from the family's variantAxes config
 */
export function parseCSVToImportProducts(
    csvRows: any[],
    brand: string,
    familyId: string,
    familySlug: string,
    variantAxes: VariantAxisConfig[] = [],
    defaultUnit: string = 'cm'
): ImportProduct[] {
    const isSurface = isSurfaceFamily(familySlug);
    const enabledAxes = variantAxes.filter(a => a.enabled);

    // Group rows by product name
    const productsByName: Record<string, any[]> = {};

    csvRows
        .filter(row => row.name)
        .forEach(row => {
            if (!productsByName[row.name]) {
                productsByName[row.name] = [];
            }
            productsByName[row.name].push(row);
        });

    // Convert to ImportProduct array
    return Object.entries(productsByName).map(([productName, rows]) => {
        const firstRow = rows[0];
        const productPlankGroupId = isSurface ? generatePlankGroupId() : '';

        // One plankGroupId per image group (surface only) — see getPlankGroupKey
        const keyToPlankGroupId: Record<string, string> = {};

        // Create variants from rows
        const variants: ImportVariant[] = rows.map((row) => {
            // Parse files from CSV - normalize Unicode only (keep quotes for filenames with commas)
            const requiredFiles = row.files
                ? row.files.split(';')
                    .map((f: string) => f
                        .trim()
                        .normalize('NFC')       // Normalize Unicode (é vs e+combining accent)
                    )
                    .filter(Boolean)
                : [];

            // Parse axis values dynamically from family config
            // CSV column name = axis name (e.g. "color-f", "size", "material", "label")
            // Fallbacks: CSV "color" column maps to axes named "label", "color-*", etc.
            const axisValues: Record<string, any> = {};
            let layoutOptions: LayoutOptions = {};
            let layoutProblem: string | undefined;
            const readLayout = (raw: unknown): string | undefined => {
                const layout = parseLayout(raw);
                layoutOptions = layout.options;
                layoutProblem = describeLayoutProblem(raw, layout.pattern, layout.unknownWords);
                return layout.pattern;
            };
            for (const axis of enabledAxes) {
                // Try exact column name first
                let rawValue = row[axis.name];

                // Fallback: "label" axis reads from CSV "color" column
                if (rawValue === undefined && axis.name === 'label') {
                    rawValue = row['color'];
                }
                // Fallback: "color-f", "color-xyz" etc. read from CSV "color" column
                if (rawValue === undefined && axis.name.startsWith('color')) {
                    rawValue = row['color'];
                }
                // Fallback: "size" axis reads from CSV "dimensions" column
                if (rawValue === undefined && axis.name === 'size') {
                    rawValue = row['dimensions'];
                }

                let parsed = parseAxisValue(rawValue, axis, defaultUnit);
                // A pattern typed any way ("Fish Scale", "Hexagon ") becomes the
                // name the texture generator knows; words after it ("stackbond:mirror")
                // become layout options.
                if (axis.name === 'pattern' && parsed !== undefined) {
                    parsed = readLayout(parsed);
                }
                if (parsed !== undefined) {
                    axisValues[axis.name] = parsed;
                }
            }

            // For surface: also read pattern from 'layout' column
            if (isSurface) {
                const patternAxis = enabledAxes.find(a => a.name === 'pattern');
                if (patternAxis && !axisValues['pattern']) {
                    axisValues['pattern'] = readLayout(row.layout) || 'bricks';
                }
                layoutProblem = layoutProblem || describeImageCountProblem(axisValues['pattern'], requiredFiles.length);
            }

            // Price, packaging and sample are per row: each variant can cost its own.
            const { warnings: priceWarnings, ...money } = parseRowCommerce(row);

            const baseVariant: ImportVariant = {
                id: uuidv4(),
                sku: String(row.sku || ''),
                requiredFiles,
                matchedFiles: [],
                isSelected: true,
                isVisible: row.is_visible !== false && row.is_visible !== 'false' && row.is_visible !== 0,
                axisValues,
                ...money,
                ...(priceWarnings.length > 0 && { priceWarnings }),
            };

            if (isSurface) {
                const groupKey = getPlankGroupKey(axisValues, enabledAxes);
                if (!keyToPlankGroupId[groupKey]) {
                    keyToPlankGroupId[groupKey] = generatePlankGroupId();
                }

                return {
                    ...baseVariant,
                    groutColor: row.grout_color || '#00000000',
                    groutSize: row.grout_size !== undefined ? Number(row.grout_size) : 0,
                    groutEditable: isCsvTrue(row.grout_editable),
                    ...(parseCsvFinish(row.finish) && { finish: parseCsvFinish(row.finish) }),
                    ...(Object.keys(layoutOptions).length > 0 && { layoutOptions }),
                    ...(layoutProblem && { layoutProblem }),
                    plankGroupId: keyToPlankGroupId[groupKey],
                };
            } else {
                return {
                    ...baseVariant,
                    modelFile: row.model_file ? String(row.model_file).trim() : undefined,
                };
            }
        });

        return {
            id: uuidv4(),
            plankGroupId: productPlankGroupId,
            familyId,
            familySlug,
            name: productName,
            // Surface uses 'type' column, non-surface uses 'category' column
            type: isSurface ? (firstRow.type || '') : (firstRow.category || ''),
            brand,
            description: firstRow.description || productName,
            link: firstRow.link || '',
            addToCartLink: firstRow.add_to_cart_link || '',
            collections: firstRow.collection ? firstRow.collection.split(';').filter(Boolean) : [],
            applicationTypes: (() => {
                const applicationRaw = firstRow.application_type || firstRow.application;
                return isSurface && applicationRaw
                    ? applicationRaw.split(';').map((e: string) => e.trim().toLowerCase().replace(/\s+/g, '')).filter(Boolean)
                    : [];
            })(),
            variants,
            // A layout with words its pattern does not accept is shown as an error
            // and never uploaded: laying the wrong pattern would go unnoticed.
            ...((): Pick<ImportProduct, 'isSelected' | 'status' | 'errorMessage' | 'layoutError'> => {
                const problem = variants.map((v: ImportVariant) => v.layoutProblem).find(Boolean);
                return problem
                    ? { isSelected: false, status: 'error', errorMessage: problem, layoutError: true }
                    : { isSelected: true, status: 'pending' };
            })(),
        };
    });
}

/**
 * Build filename lookup map for O(1) image matching
 * Stores multiple key variations to maximize matches
 */
export function buildFileLookupMap(products: ImportProduct[]): Map<string, { productIndex: number; variantIndex: number; isModelFile?: boolean }> {
    const map = new Map<string, { productIndex: number; variantIndex: number; isModelFile?: boolean }>();
    let totalFiles = 0;

    products.forEach((product, productIndex) => {
        product.variants.forEach((variant, variantIndex) => {
            // Index required image files
            variant.requiredFiles.forEach(fileName => {
                totalFiles++;
                const entry = { productIndex, variantIndex };

                // Get base name (remove path only, keep quotes for filenames with commas)
                const baseName = fileName.split('/').pop() || '';

                // Store multiple variations for robust matching:
                // 1. Original from CSV (may include quotes)
                map.set(baseName, entry);
                // 2. Trimmed version
                map.set(baseName.trim(), entry);
                // 3. Normalized (lowercase, NFC, etc.)
                map.set(normalizeFilename(baseName), entry);
                // 4. Just NFC normalized (preserve case) for exact Unicode matches
                map.set(baseName.trim().normalize('NFC'), entry);
            });

            // Index model files (furniture/lighting)
            if (variant.modelFile) {
                totalFiles++;
                const modelEntry = { productIndex, variantIndex, isModelFile: true };
                const baseName = variant.modelFile.split('/').pop() || '';

                map.set(baseName, modelEntry);
                map.set(baseName.trim(), modelEntry);
                map.set(normalizeFilename(baseName), modelEntry);
                map.set(baseName.trim().normalize('NFC'), modelEntry);
                // A model_file cell written without its extension ("zareh") still
                // matches the dropped zareh.glb / zareh.gltf.
                if (!/\.\w+$/.test(baseName.trim())) {
                    map.set(normalizeFilename(`${baseName}.glb`), modelEntry);
                    map.set(normalizeFilename(`${baseName}.gltf`), modelEntry);
                }
            }
        });
    });

    // Debug: log some sample required files from CSV
    const sampleRequiredFiles: string[] = [];
    products.slice(0, 3).forEach(p => {
        p.variants.slice(0, 1).forEach(v => {
            v.requiredFiles.slice(0, 2).forEach(f => {
                sampleRequiredFiles.push(f);
            });
        });
    });
    console.log(`[buildFileLookupMap] Built map with ${map.size} keys from ${totalFiles} required files across ${products.length} products`);
    console.log(`[buildFileLookupMap] Sample required files from CSV:`, sampleRequiredFiles);

    return map;
}

/**
 * Normalize filename for matching
 * Uses NFC normalization for Unicode (handles accented characters like é, è, ô)
 * Converts to lowercase and normalizes whitespace/separators
 * Keeps extension for accurate matching
 */
export function normalizeFilename(filename: string): string {
    let result = filename
        .trim()
        .replace(/^["']|["']$/g, '')                   // Remove surrounding quotes FIRST
        .normalize('NFC')                              // Unicode normalization for accented chars
        .toLowerCase()                                 // Case insensitive
        .replace(/×/g, 'x')                            // Unicode multiplication sign -> x
        .replace(/\*/g, 'x')                           // Asterisk -> x (CSV uses * for dimensions)
        .replace(/_copie(\.\w+)$/i, '$1')              // Remove "_copie" suffix before extension
        .replace(/ copie(\.\w+)$/i, '$1')              // Remove " copie" suffix before extension
        .replace(/ - copie(\.\w+)$/i, '$1')            // Remove " - copie" suffix before extension
        .replace(/\s+/g, '_')                          // All spaces -> underscore
        .replace(/[_\-]+/g, '_')                       // Normalize all separators to single underscore
        .replace(/-(\d+)(\.\w+)$/i, '_$1$2')           // Normalize -1.jpg to _1.jpg
        .replace(/\.jpeg$/i, '.jpg');                  // Normalize .jpeg -> .jpg

    // Final cleanup: remove trailing underscore before extension (run last to catch all cases)
    result = result.replace(/_+(\.\w+)$/i, '$1');

    return result;
}

/**
 * Extract keywords from filename for fuzzy matching
 */
export function extractKeywords(filename: string): string[] {
    // Remove extension and quotes
    const withoutExt = filename.replace(/^["']|["']$/g, '').replace(/\.\w+$/, '');
    // Extract words (split on spaces, hyphens, underscores, asterisks)
    return withoutExt.split(/[\s\-_*]+/).filter(w => w.length > 2).map(w => w.toLowerCase());
}

/**
 * Extract colors from an image file (resized for speed)
 */
export async function extractColorsFromImage(
    file: File,
    extractColorsFromSrc: (src: string, options: any) => Promise<{ hex: string }[]>
): Promise<string[]> {
    return new Promise((resolve) => {
        const reader = new FileReader();

        reader.onload = (event) => {
            const imageUrl = event.target?.result as string;

            // Create small canvas for fast extraction
            const img = new Image();
            img.onload = async () => {
                try {
                    const canvas = document.createElement('canvas');
                    const size = 100; // Small size for speed
                    canvas.width = size;
                    canvas.height = size;
                    const ctx = canvas.getContext('2d');

                    if (!ctx) {
                        resolve(['#907361']);
                        return;
                    }

                    ctx.drawImage(img, 0, 0, size, size);
                    const smallImageUrl = canvas.toDataURL('image/jpeg', 0.8);

                    const colors = await extractColorsFromSrc(smallImageUrl, {
                        distance: 0.2,
                        saturationDistance: 0.5,
                        lightnessDistance: 0.1
                    });

                    const hexCodes = colors.map(c => c.hex);
                    resolve(hexCodes.length > 0 ? hexCodes : ['#907361']);
                } catch (error) {
                    console.error('[ColorExtraction] Error:', error);
                    resolve(['#907361']);
                }
            };

            img.onerror = () => resolve(['#907361']);
            img.src = imageUrl;
        };

        reader.onerror = () => resolve(['#907361']);
        reader.readAsDataURL(file);
    });
}

/**
 * Build API payload from ImportProduct
 * Creates V3 CreateProductV2Payload structure for the /v2/products/bulk endpoint
 * Adapts visualization type and file naming based on family
 */
export function buildAPIPayload(
    product: ImportProduct,
    extractedColors: string[] = ['#907361'],
): APIPayloadResult {
    const products: CreateProductV2Payload[] = [];
    const files: PreparedFile[] = [];

    // Shared product group ID for all variants
    const productGroupId = product.id;
    const vizType = getVisualizationTypeForFamily(product.familySlug);
    const isSurface = isSurfaceFamily(product.familySlug);
    // Source file names already sent per plankGroupId
    const plankFilesSent = new Map<string, Set<string>>();

    // Process each selected variant
    product.variants
        .filter(v => v.isSelected)
        .forEach((variant, variantIndex) => {
            const isDefault = variantIndex === 0;

            // Build axes from variant's parsed axisValues
            // The built-in 'color' axis in ProductAxes uses ColorAxisSchema { value, hex? }
            // Custom color axes (e.g. 'color-f') are stored as plain strings
            const axes: Record<string, any> = {};
            for (const [key, val] of Object.entries(variant.axisValues)) {
                if (key === 'color' && typeof val === 'string') {
                    // Built-in color axis — wrap in ColorAxis object
                    axes[key] = { value: val };
                } else {
                    axes[key] = val;
                }
            }

            // Build visualization based on type
            let visualization: any;
            if (vizType === 'surface') {
                const sizeAxis = variant.axisValues.size || {};
                const pattern = variant.axisValues.pattern || 'bricks';
                visualization = {
                    type: 'surface',
                    surface: {
                        plankGroupId: variant.plankGroupId,
                        grout: { color: variant.groutColor || '#00000000', width: variant.groutSize || 0 },
                        ...(variant.groutEditable && { groutEditable: true }),
                        ...(variant.finish && { finish: variant.finish }),
                        // No textureWidth/Height: the worker writes the real size the
                        // generator returns (see buildV2Operations).
                        seamlessParams: {
                            pattern,
                            plankWidth: plankCm(sizeAxis.width, sizeAxis.unit),
                            plankHeight: plankCm(sizeAxis.height, sizeAxis.unit),
                            // Fully zoomed out, matching the thumbnail the generator
                            // cuts for imports; the editor's cropper reopens at this.
                            zoom: 1,
                            crop: { x: 0, y: 0 },
                            ...variant.layoutOptions,
                        }
                    }
                };
            } else if (vizType === 'model3d') {
                visualization = {
                    type: 'model3d',
                    model3d: {
                        modelPath: '',
                        originalImagePath: '',
                    }
                };
            } else {
                // image_only
                visualization = {
                    type: 'image_only',
                };
            }

            const apiProduct: CreateProductV2Payload = {
                id: productGroupId,
                uuid: variant.id,
                vendor: '', // Backend sets from JWT token
                familyId: product.familyId,
                familySlug: product.familySlug,
                name: product.name,
                brand: product.brand,
                description: product.description,
                sku: variant.sku,
                axes,
                visualization,
                attributes: {
                    colors: extractedColors,
                    collections: product.collections
                },
                organization: {
                    productType: product.type,
                    applicationTypes: product.applicationTypes
                },
                commerce: {
                    link: product.link,
                    addToCartLink: product.addToCartLink,
                    // What this row sells for; the bulk worker stores commerce as sent.
                    ...(variant.price && { price: variant.price }),
                    ...(variant.pack && { pack: variant.pack }),
                    ...(variant.sample && { sample: variant.sample }),
                },
                isDefault,
                isVisible: variant.isVisible,
                active: true,
                siblingOrder: variantIndex,
                siblings: []
            };

            products.push(apiProduct);

            // Collect files based on family type
            if (isSurface) {
                // Surface: plank images with material_ prefix naming
                // Format: material_{plankGroupId}_{index}.{ext} — matches backend regex /^(material_[a-f0-9]+)_(\d+)\./i
                // The API pools every file named for a group into that group's image
                // set, so a group's images go up once: pattern variants list the same
                // files, and sending them per variant duplicated each one.
                const groupId = variant.plankGroupId || '';
                const sent = plankFilesSent.get(groupId) || new Set<string>();
                plankFilesSent.set(groupId, sent);
                variant.matchedFiles.forEach((file) => {
                    if (sent.has(file.name)) return;
                    const ext = file.name.split('.').pop()?.toLowerCase() || 'png';
                    files.push({
                        file: new File([file], `${groupId}_${sent.size}.${ext}`, { type: file.type }),
                        metaType: 'plank',
                        productId: groupId
                    });
                    sent.add(file.name);
                });
            } else {
                // Non-surface: product image + thumbnail + optional 3D model
                variant.matchedFiles.forEach((file, fileIndex) => {
                    const ext = file.name.split('.').pop()?.toLowerCase() || 'png';

                    if (fileIndex === 0) {
                        // First image: send as both image_{uuid} (originalImagePath) and thumbnail_{uuid} (thumbnailPath)
                        files.push({
                            file: new File([file], `image_${variant.id}.${ext}`, { type: file.type }),
                            metaType: 'product_image',
                            productId: variant.id
                        });
                        files.push({
                            file: new File([file], `thumbnail_${variant.id}.${ext}`, { type: file.type }),
                            metaType: 'thumbnail',
                            productId: variant.id
                        });
                    } else {
                        // Additional images
                        files.push({
                            file: new File([file], `image_${variant.id}_${fileIndex}.${ext}`, { type: file.type }),
                            metaType: 'product_image',
                            productId: variant.id
                        });
                    }
                });

                // 3D model file (furniture/lighting)
                if (variant.matchedModelFile) {
                    const ext = variant.matchedModelFile.name.split('.').pop()?.toLowerCase() || 'glb';
                    files.push({
                        file: new File([variant.matchedModelFile], `material_${variant.id}.${ext}`, { type: variant.matchedModelFile.type }),
                        metaType: 'model3d',
                        productId: variant.id
                    });
                }
            }
        });

    return { products, files };
}

/**
 * Build V2 bulk operations wrapper from ImportProduct
 * Wraps products in BulkOperationsV2Dto format for the /v2/products/bulk endpoint
 * Only generates seamless textures for surface product families
 */
export function buildV2Operations(
    product: ImportProduct,
    extractedColors: string[] = ['#907361'],
): V2OperationsResult {
    const { products, files } = buildAPIPayload(product, extractedColors);
    const isSurface = isSurfaceFamily(product.familySlug);

    const operations: BulkOperationItem[] = products.map(productPayload => {
        const op: BulkOperationItem = {
            operation: 'create' as const,
            data: productPayload,
            generateSeamless: isSurface,
        };

        // Only include seamlessParams for surface products.
        // Leave the texture size out, as the editor's Regenerate does: the generator
        // then sizes the repeat to the tiles and keeps the planks' pixel density.
        // A forced 4m x 4m came back as a large 500px/m texture, and imported
        // products needed a manual Regenerate before they showed at the right size.
        if (isSurface) {
            op.seamlessParams = {
                pattern: (productPayload.axes?.pattern as string) || 'bricks',
                groutLineColor: productPayload.visualization?.surface?.grout?.color || '#00000000',
                groutLineWidth: productPayload.visualization?.surface?.grout?.width || 0,
                plankWidth: plankCm((productPayload.axes?.size as any)?.width, (productPayload.axes?.size as any)?.unit),
                plankHeight: plankCm((productPayload.axes?.size as any)?.height, (productPayload.axes?.size as any)?.unit),
                ...pickLayoutOptions(productPayload.visualization?.surface?.seamlessParams),
            };
        }

        return op;
    });

    return {
        operations: {
            productGroupId: product.id,
            familyId: product.familyId,
            familySlug: product.familySlug,
            operations
        },
        files
    };
}

/*
 * ONE rule decides whether a row is complete, and every count on the import screen
 * reads it: the row badge, the per-variant cells, the summary cards and the
 * "Upload Selected" button (ARDE-270). A variant needs every image its CSV `files`
 * cell names AND, when its `model_file` cell names one, that 3D model — whatever the
 * family (a rug in Decor can name a .glb as well as a sofa in Furniture). Surface
 * rows never carry a model, so for them this is the plank-image count alone.
 */

/** Files a variant still has to receive: its images plus the 3D model its CSV names. */
export function getVariantRequiredFileCount(variant: ImportVariant): number {
    return variant.requiredFiles.length + (variant.modelFile ? 1 : 0);
}

/** Files matched to a variant: its images plus its 3D model once that has arrived. */
export function getVariantMatchedFileCount(variant: ImportVariant): number {
    // Capped per kind so an extra copy of one image cannot stand in for a missing model.
    const images = Math.min(variant.matchedFiles.length, variant.requiredFiles.length);
    return images + (variant.modelFile && variant.matchedModelFile ? 1 : 0);
}

/** A variant is complete when it has every image and, if its CSV names one, its model. */
export function variantHasAllFiles(variant: ImportVariant): boolean {
    const hasImages = variant.matchedFiles.length >= variant.requiredFiles.length;
    const hasModel = !variant.modelFile || !!variant.matchedModelFile;
    return hasImages && hasModel;
}

/**
 * Get total matched file count for a product (images + matched 3D models)
 */
export function getProductMatchedFileCount(product: ImportProduct): number {
    return product.variants.reduce((sum, v) => sum + getVariantMatchedFileCount(v), 0);
}

/**
 * Get total required file count for a product (images + 3D models the CSV names).
 * Counting the model keeps the count honest: a row whose model is missing must not
 * read as complete while its Save button stays disabled.
 */
export function getProductRequiredFileCount(product: ImportProduct): number {
    return product.variants.reduce((sum, v) => sum + getVariantRequiredFileCount(v), 0);
}

/**
 * Check if product has all required files (images + model files if applicable)
 */
export function productHasAllFiles(product: ImportProduct): boolean {
    return product.variants.every(variantHasAllFiles);
}

/** A product the upload button will send: complete, not sent yet, layout valid. */
export function productIsUploadable(product: ImportProduct): boolean {
    return product.status !== 'done'
        && product.status !== 'uploading'
        && !product.layoutError
        && productHasAllFiles(product);
}

export interface ImportSummary {
    total: number;
    selected: number;
    /** Not uploaded yet and ready to go — the "Ready to Upload" card. */
    ready: number;
    /** Not uploaded yet and still missing images or a 3D model. */
    awaitingFiles: number;
    done: number;
    errors: number;
    /** Selected products the upload button will send — its "(n)". */
    selectedReady: number;
    /** Selected, not uploaded, but held back (missing files or an invalid layout). */
    selectedNotReady: number;
    /** Whether any row names a 3D model, so copy can say "files" instead of "images". */
    hasModelFiles: boolean;
}

/** Every count on the import screen, from the same rule as the row badge. */
export function summarizeImport(products: ImportProduct[], selectedIds: Set<string>): ImportSummary {
    let ready = 0, awaitingFiles = 0, done = 0, errors = 0, selected = 0, selectedReady = 0, selectedNotReady = 0;
    let hasModelFiles = false;
    for (const p of products) {
        const isSelected = selectedIds.has(p.id);
        const uploadable = productIsUploadable(p);
        if (isSelected) selected++;
        if (p.status === 'done') done++;
        if (p.status === 'error') errors++;
        if (p.status !== 'done') {
            if (uploadable) ready++;
            if (!productHasAllFiles(p)) awaitingFiles++;
            if (isSelected) {
                if (uploadable) selectedReady++;
                else if (p.status !== 'uploading') selectedNotReady++;
            }
        }
        if (!hasModelFiles && p.variants.some(v => v.modelFile)) hasModelFiles = true;
    }
    return { total: products.length, selected, ready, awaitingFiles, done, errors, selectedReady, selectedNotReady, hasModelFiles };
}
